import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useInvestorAuth } from '../../context/InvestorAuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_STYLE = {
  submitted:   { bg: '#f3f4f6', color: '#374151', label: 'Under Review' },
  loi_pending: { bg: '#fffde4', color: '#111111', label: 'LOI Pending' },
  loi_signed:  { bg: '#eff4ff', color: '#3a6ad9', label: 'LOI Signed' },
  dd_open:     { bg: '#f0fde4', color: '#111111', label: 'DD Room Open' },
  rejected:    { bg: '#ffe6e9', color: '#ff082f', label: 'Not Selected' },
  withdrawn:   { bg: '#f3f4f6', color: '#6b7280', label: 'Withdrawn' },
};

function fmt$(n) {
  const v = Number(n) || 0;
  return v ? `$${Math.round(v).toLocaleString()}` : '—';
}
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function MarketBidsList() {
  const { token, investor, investorLogout } = useInvestorAuth();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/market/bids`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) setBids(res.data.bids || []);
      } catch (err) {
        if (mounted) setError(err.response?.data?.error || 'Failed to load your bids.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <header style={{ backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/market/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <img src="/doorly-market-logo.png" alt="Doorly Market" style={{ height: '32px', width: 'auto', display: 'block' }} />
        </Link>
        <nav style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          <Link to="/market/dashboard" style={navLink}>Dashboard</Link>
          <Link to="/market/pools" style={navLink}>Pools</Link>
          <Link to="/market/bids" style={{ ...navLink, color: '#3a6ad9' }}>My Bids</Link>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            {investor?.firstName} {investor?.lastName}
          </span>
          <button onClick={investorLogout} style={{ backgroundColor: 'transparent', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', color: '#374151' }}>Sign out</button>
        </nav>
      </header>

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 24px 64px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>My Bids</h1>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 18px' }}>
          Every bid you've submitted, with status and next steps.
        </p>

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 24, color: '#ff082f', backgroundColor: '#fff5f6', border: '1px solid #ffd6db', borderRadius: 12 }}>{error}</div>
        ) : bids.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#6b7280', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 6 }}>No bids yet</div>
            <p style={{ fontSize: 13, marginBottom: 12 }}>
              Browse available pools and submit your first bid.
            </p>
            <Link to="/market/pools" style={{ display: 'inline-block', backgroundColor: '#3a6ad9', color: '#fff', padding: '10px 18px', borderRadius: 100, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
              Browse Pools →
            </Link>
          </div>
        ) : (
          <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  {['Pool', 'Bid %', 'Bid Amount', 'Status', 'Submitted', 'Actions'].map((h) => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bids.map((b) => {
                  const s = STATUS_STYLE[b.status] || { bg: '#f3f4f6', color: '#374151', label: b.status };
                  return (
                    <tr key={b.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: '#111827' }}>{b.pool.poolName}</div>
                        <div style={{ color: '#6b7280', fontSize: 12 }}>{b.pool.poolNumber}</div>
                      </td>
                      <td style={td}>{Number(b.bidPricePct).toFixed(2)}%</td>
                      <td style={td}>{fmt$(b.bidAmount)}</td>
                      <td style={td}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 9999, backgroundColor: s.bg, color: s.color, fontSize: 11, fontWeight: 700 }}>
                          {s.label}
                        </span>
                      </td>
                      <td style={{ ...td, color: '#6b7280' }}>{fmtDate(b.submittedAt)}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Link to={`/market/pools/${b.pool.id}`} style={linkBtn}>View Pool</Link>
                          {b.status === 'dd_open' && (
                            <Link to={`/market/bids/${b.id}/dd-room`} style={{ ...linkBtn, backgroundColor: '#acf44e', color: '#fff', borderColor: '#acf44e' }}>
                              Enter DD Room
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

const navLink = { fontSize: 13, fontWeight: 600, color: '#374151', textDecoration: 'none' };
const td = { padding: '12px 16px', verticalAlign: 'middle', color: '#111827' };
const linkBtn = { display: 'inline-block', padding: '5px 11px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#374151', backgroundColor: '#fff', border: '1px solid #e5e7eb', textDecoration: 'none' };
